import { getRecoil, setRecoil } from 'recoil-nexus';
import { cartState } from '../atoms/cartAtom';
import facebookConversionService, { categoryPixel } from './facebookConversionService';

export const CartService = {
  addToCart: (product, quantity = 1, variant = null) => {
    const cart = getRecoil(cartState);
    const newItems = [...(cart.items || [])];
    
    // For this specific data structure, always use the product price
    // since variants don't have their own prices
    let productPrice = parseFloat(product.prixVenteTTC || 0);

    console.log('Using product price:', productPrice);

    // Ensure we have a number and not NaN
    if (isNaN(productPrice)) {
      console.error('Failed to extract price from product:', { product });
      productPrice = 0;
    }
    
    // Generate a unique cart item ID
    const cartItemId = `${product.id}${variant ? `-${variant.id}` : ''}`;
    
    // Check if item already exists in cart
    const existingItemIndex = newItems.findIndex(item => 
      item.cartItemId === cartItemId
    );
    
    if (existingItemIndex >= 0) {
      // Update existing item
      newItems[existingItemIndex] = {
        ...newItems[existingItemIndex],
        quantity: newItems[existingItemIndex].quantity + quantity,
        price: productPrice // Use the product price
      };
    } else {
      // Add new item with the product price
      newItems.push({
        cartItemId,
        productId: product.id,
        variantId: variant ? variant.id : null,
        name: product.name + (variant ? ` (${variant.name})` : ''),
        price: productPrice, // Use the product price
        quantity,
        image: variant && variant.imageUrl && variant.imageUrl.length > 0 
          ? variant.imageUrl[0] 
          : (product.imageUrl && product.imageUrl.length > 0 
              ? product.imageUrl[0] 
              : ''),
        variantName: variant ? variant.name : '',
        reference: variant ? variant.reference : product.reference,
        stock: variant ? variant.stock : product.stock,
        livraisonGratuite: product.livraisonGratuite || false,
        seuilLivraisonGratuite: product.seuilLivraisonGratuite || 0,
        // Facebook pixel of the product's category: the checkout sends each pixel its own items.
        pixelId: categoryPixel(product.categorie)?.pixelId || null,
        pixelHasToken: categoryPixel(product.categorie)?.hasToken || false
      });
    }
    
    // Calculate cart totals (safely with Number conversions)
    const count = newItems.reduce((sum, item) => sum + Number(item.quantity || 0), 0);
    
    // Ensure we're calculating with numbers, not strings
    const total = newItems.reduce((sum, item) => {
      const itemPrice = Number(item.price || 0);
      const itemQuantity = Number(item.quantity || 0);
      return sum + (itemPrice * itemQuantity);
    }, 0);

    console.log('Cart calculation:', {
      items: newItems.map(item => ({
        name: item.name,
        price: item.price,
        quantity: item.quantity,
        total: item.price * item.quantity
      })),
      count,
      total
    });

    // Update cart state
    setRecoil(cartState, {
      ...cart,
      items: newItems,
      count,
      total
    });
    
    // Save to localStorage
    localStorage.setItem('cart', JSON.stringify({
      items: newItems,
      count,
      total
    }));
    
    // Track Facebook AddToCart event (only for newly added items, not updates)
    if (existingItemIndex === -1) {
      try {
        facebookConversionService.trackAddToCart({
          id: product.id,
          price: productPrice,
          name: product.name,
          quantity,
        }, product.categorie);
      } catch (error) {
        // Silently fail - don't block cart operations
        console.error('Facebook AddToCart tracking failed:', error);
      }
    }
    
    return { success: true, items: newItems, count, total };
  },
  
  removeFromCart: (cartItemId) => {
    const cart = getRecoil(cartState);
    
    const newItems = (cart.items || []).filter(item => item.cartItemId !== cartItemId);
    
    // Recalculate totals (safely)
    const count = newItems.reduce((sum, item) => sum + Number(item.quantity || 0), 0);
    const total = newItems.reduce((sum, item) => {
      const itemPrice = Number(item.price || 0);
      const itemQuantity = Number(item.quantity || 0);
      return sum + (itemPrice * itemQuantity);
    }, 0);
    
    // Update cart state
    setRecoil(cartState, {
      ...cart,
      items: newItems,
      count,
      total
    });
    
    // Save to localStorage
    localStorage.setItem('cart', JSON.stringify({
      items: newItems,
      count,
      total
    }));
    
    return { success: true, items: newItems, count, total };
  },
  
  updateQuantity: (cartItemId, quantity) => {
    const cart = getRecoil(cartState);
    
    const newItems = (cart.items || []).map(item => 
      item.cartItemId === cartItemId 
        ? { ...item, quantity: Math.max(1, quantity) } 
        : item
    );
    
    // Recalculate totals (safely)
    const count = newItems.reduce((sum, item) => sum + Number(item.quantity || 0), 0);
    const total = newItems.reduce((sum, item) => {
      const itemPrice = Number(item.price || 0);
      const itemQuantity = Number(item.quantity || 0);
      return sum + (itemPrice * itemQuantity);
    }, 0);
    
    // Update cart state
    setRecoil(cartState, {
      ...cart,
      items: newItems,
      count,
      total
    });
    
    // Save to localStorage
    localStorage.setItem('cart', JSON.stringify({
      items: newItems,
      count,
      total
    }));
    
    return { success: true, items: newItems, count, total };
  },
  
  clearCart: () => {
    setRecoil(cartState, {
      items: [],
      count: 0,
      total: 0,
      isOpen: false
    });
    
    localStorage.removeItem('cart');
    
    return { success: true };
  },
  
  toggleCart: () => {
    const cart = getRecoil(cartState);
    setRecoil(cartState, {
      ...cart,
      isOpen: !cart.isOpen
    });
  },
  
  loadCart: () => {
    const savedCart = localStorage.getItem('cart');
    
    if (savedCart) {
      try {
        const parsedCart = JSON.parse(savedCart);
        
        // Validate and fix any potential issues with the loaded cart
        const validatedItems = (parsedCart.items || []).map(item => ({
          ...item,
          price: Number(item.price || 0),
          quantity: Number(item.quantity || 1)
        }));
        
        // Recalculate totals to ensure consistency
        const count = validatedItems.reduce((sum, item) => sum + Number(item.quantity || 0), 0);
        const total = validatedItems.reduce((sum, item) => sum + (Number(item.price || 0) * Number(item.quantity || 0)), 0);
        
        setRecoil(cartState, {
          items: validatedItems,
          count: count,
          total: total,
          isOpen: false
        });
        
        console.log('Cart loaded from localStorage:', {
          items: validatedItems,
          count,
          total
        });
      } catch (error) {
        console.error('Error parsing cart from localStorage:', error);
        // Reset to empty cart if there was an error
        setRecoil(cartState, {
          items: [],
          count: 0,
          total: 0,
          isOpen: false
        });
      }
    }
  }
};